import { useRef, useState, useEffect } from 'react';
import './violations.css';
import updateUserInterviewDetails from '../../apis/candidatescreening/updateuserinterviewdetails';
import vetcandidateviolation from '../../apis/vettest/vetcandidateviolation';



export const Violations = ({ 
    violations: mediapipeViolations, 
    timer, 
    userId, 
    jdId, 
    isInterviewCompleted, 
    isVet = false,
    sectionId = null,
    questionId = null
}) => {
    const intervalRef = useRef(null);
    const [violations, setViolations] = useState({
        face: {
            flagged: false,
            count: 0,
            events: []
        },
        voice: {
            flagged: false,
            count: 0,
            events: []
        },
        browser: {
            flagged: false,
            count: 0,
            events: []
        }
    });

    const violationsRef = useRef(violations);
    violationsRef.current = violations;

    const prevViolationsRef = useRef();
    const [lastSentViolations, setLastSentViolations] = useState(new Set());

    useEffect(() => {
        const addViolation = (violationType, category) => {
            const atSec = timer;
            const severity = 'high';

            const newEvent = { atSec, type: violationType, severity };
            setViolations(prev => {
                const updatedCategory = {
                    ...prev[category],
                    flagged: true,
                    count: prev[category].count + 1,
                    events: [...prev[category].events, newEvent],
                };
                return { ...prev, [category]: updatedCategory };
            });
        };

        if (!mediapipeViolations || !prevViolationsRef.current) {
            prevViolationsRef.current = mediapipeViolations;
            return;
        }

        const prevMViolations = prevViolationsRef.current;

        for (const key in mediapipeViolations.violations) {
            const currentViolation = mediapipeViolations.violations[key];
            const prevViolation = prevMViolations.violations[key];

            if (currentViolation?.state && !prevViolation?.state) {
                addViolation(key, currentViolation.category);
            }
        }

        prevViolationsRef.current = mediapipeViolations;
    }, [mediapipeViolations, timer]);

    // TAB_SWITCH is emitted by useMediaPipeViolations (visibilitychange listener lives there)
    // and flows in via mediapipeViolations.violations.TAB_SWITCH.

    const buildVetViolationPayload = (violationsData, candidateTestId, currentSectionId, currentQuestionId) => {
        const payload = {
            candidateTestId: candidateTestId,
            violations: []
        };

        if (violationsData.face?.events?.length > 0) {
            const faceViolations = violationsData.face.events.map(event => ({
                violationSource: 'FACE',
                violationType: mapFaceViolationType(event.type),
                severity: (event.severity || 'medium').toLowerCase(),
                atSec: event.atSec || 0,
                sectionId: currentSectionId || null,
                questionId: currentQuestionId || null
            }));
            payload.violations.push(...faceViolations);
        }

        if (violationsData.voice?.events?.length > 0) {
            const voiceViolations = violationsData.voice.events.map(event => ({
                violationSource: 'VOICE',
                violationType: mapVoiceViolationType(event.type),
                severity: (event.severity || 'medium').toLowerCase(),
                atSec: event.atSec || 0,
                sectionId: currentSectionId || null,
                questionId: currentQuestionId || null
            }));
            payload.violations.push(...voiceViolations);
        }

        if (violationsData.browser?.events?.length > 0) {
            const browserViolations = violationsData.browser.events.map(event => ({
                violationSource: "BROWSER",
                violationType: event.type,
                severity: (event.severity || "high").toLowerCase(),
                atSec: event.atSec || 0,
                sectionId: currentSectionId || null,
                questionId: currentQuestionId || null
            }));

            payload.violations.push(...browserViolations);
        }

        return payload;
    };

    const mapFaceViolationType = (type) => {
        const typeMap = {
            NO_FACE: "NO_FACE",
            MULTI_FACE: "MULTI_FACE",
            LOOKING_AWAY: "LOOKING_AWAY",
        };

        return typeMap[type] || type;
    };

    const mapVoiceViolationType = (type) => {
        const typeMap = {
            LIPS_NOT_MOVING: "LIPS_NOT_MOVING",
            MULTI_SPEAKER: "MULTI_SPEAKER",
        };

        return typeMap[type] || type;
    };

    const sendVetViolations = async (currentViolations) => {
        if (!userId) return;

        try {
            const payload = buildVetViolationPayload(
                currentViolations,
                userId,
                sectionId,
                questionId
            );

            if (!payload.violations.length) {
                return;
            }
            const newViolations = payload.violations.filter((violation) => {
                const key = `${violation.violationSource}-${violation.violationType}-${violation.atSec}`;
                return !lastSentViolations.has(key);
            });

            if (!newViolations.length) {
                return;
            }

            const newPayload = {
                ...payload,
                violations: newViolations
            };

            await vetcandidateviolation(newPayload);

            // Update sent violations set
            setLastSentViolations((prev) => {
                const updated = new Set(prev);
                newViolations.forEach((violation) => {
                    const key = `${violation.violationSource}-${violation.violationType}-${violation.atSec}`;
                    updated.add(key);
                });
                return updated;
            });

        } catch (error) {
            console.error("Error sending VET violations:", error);
        }
    };

    useEffect(() => {
        if (isInterviewCompleted) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        if (!isVet && userId && jdId && !intervalRef.current) {
            intervalRef.current = setInterval(() => {
                const currentViolations = violationsRef.current;
                const backendViolations = {
                    face: currentViolations.face,
                    voice: currentViolations.voice,
                    browser: currentViolations.browser
                };
                updateUserInterviewDetails(userId, jdId, { violations: backendViolations });
            }, 5000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [userId, jdId, isInterviewCompleted, isVet]);

    useEffect(() => {
        if (!isVet || !userId || isInterviewCompleted) {
            return;
        }
        sendVetViolations(violations);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [violations]);

    return (
        <>
            {/* <Paper className="violations-paper">
                <Box className="violations-header">
                    <Typography className="violations-header-title">
                        Violations / Info
                    </Typography>
                </Box>
                <Box className="violations-scroll-container">
                    <ViolationItem id="audioViolation" isActive={isAudioViolation} count={violations.voice.count}>
                        Voice violations detected
                    </ViolationItem>
                    <ViolationItem id="videoViolation" isActive={isVideoViolation} count={violations.face.count}>
                        Face violations detected
                    </ViolationItem>
                    <ViolationItem id="tabSwitchViolation" isActive={isTabSwitchViolation} count={tabSwitchCount}>
                        Tab switch violations detected
                    </ViolationItem>
                </Box>
            </Paper> */}
        </>
    );
};

export default Violations;